import * as R from 'ramda'
import { useState, useEffect, useRef } from 'react'
import cx from 'classnames'

import { TOPICS } from '../constants'
import s from './styles.module.css'

const OPEN_DELAY = 1500

const VennDiagram = ({ setTopic, topic }) => {
  const svgEl = useRef(null)
  const [isClosed, setIsClosed] = useState(true)
  const [hasOpened, setHasOpened] = useState(false)

  const onClick = () => {
    setIsClosed(R.not)
    setTopic(null)
  }

  useEffect(() => {
    const onScroll = () => {
      if (hasOpened) return

      const { bottom } = svgEl.current.getBoundingClientRect()
      const height = svgEl.current.clientHeight / 2
      const fromBottom = bottom - window.innerHeight - height

      if (fromBottom < (window.innerWidth <= 992 ? -100 : -200)) {
        setTimeout(() => {
          setIsClosed(false)
          setHasOpened(true)
        }, OPEN_DELAY)
      }
    }

    window.addEventListener('scroll', onScroll)
    onScroll()

    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, [hasOpened])

  return (
    <svg
      ref={svgEl}
      onClick={isClosed ? onClick : undefined}
      viewBox="0 0 450 450"
      xmlns="http://www.w3.org/2000/svg"
      className={cx(s.vennDiagram, {
        [s.vennDiagramClosed]: isClosed,
        [s.vennDiagramOpen]: !isClosed,
        [s.vennDiagramAActive]: topic === TOPICS.sleep,
        [s.vennDiagramBActive]: topic === TOPICS.nutrition,
        [s.vennDiagramCActive]: topic === TOPICS.exercise,
      })}>
      <g
        className={cx(s.vennArea, s.vennCircle, s.vennSetsA, {
          [s.vennCircleActive]: topic === TOPICS.sleep,
        })}
        onClick={() => setTopic(TOPICS.sleep)}
        onMouseEnter={() => setTopic(TOPICS.sleep)}>
        <path
          data-step="4"
          d="
    M 171.95623943534935 250.92756433859927
    m -118.07243566140069 0
    a 118.07243566140069 118.07243566140069 0 1 0 236.14487132280138 0
    a 118.07243566140069 118.07243566140069 0 1 0 -236.14487132280138 0"></path>

        <text data-step="4" textAnchor="middle" dy=".35em" x="130" y="259">
          <tspan x="130" y="259" dy="0.35em">
            Sleep
          </tspan>
        </text>
      </g>

      <g
        className={cx(s.vennArea, s.vennCircle, s.vennSetsB, {
          [s.vennCircleActive]: topic === TOPICS.nutrition,
        })}
        onClick={() => setTopic(TOPICS.nutrition)}
        onMouseEnter={() => setTopic(TOPICS.nutrition)}>
        <path
          data-step="5"
          d="
    M 308.04376056465065 250.92756433859927
    m -118.07243566140069 0
    a 118.07243566140069 118.07243566140069 0 1 0 236.14487132280138 0
    a 118.07243566140069 118.07243566140069 0 1 0 -236.14487132280138 0"></path>

        <text data-step="5" textAnchor="middle" dy=".35em" x="350" y="259">
          <tspan x="350" y="259" dy="0.35em">
            Nutrition
          </tspan>
        </text>
      </g>

      <g
        className={cx(s.vennArea, s.vennCircle, s.vennSetsC, {
          [s.vennCircleActive]: topic === TOPICS.exercise,
        })}
        onClick={() => setTopic(TOPICS.exercise)}
        onMouseEnter={() => setTopic(TOPICS.exercise)}>
        <path
          data-step="3"
          d="
    M 239.9997461549209 133.07243566140068
    m -118.07243566140069 0
    a 118.07243566140069 118.07243566140069 0 1 0 236.14487132280138 0
    a 118.07243566140069 118.07243566140069 0 1 0 -236.14487132280138 0"></path>
        <text data-step="3" textAnchor="middle" dy=".35em" x="241" y="110">
          <tspan x="241" y="110" dy="0.35em">
            Exercise
          </tspan>
        </text>
        <text
          className={s.middleText}
          textAnchor="middle"
          dy=".35em"
          x="239"
          y="131">
          <tspan x="239" y="131" dy="0.35em">
            Accountability
          </tspan>
        </text>
      </g>

      <g className={cx(s.vennArea, s.vennIntersection, s.vennSetsAB)}>
        <path
          data-intersection="4-5"
          d="
    M 240 347.4218476471772
    A 118.07243566140069 118.07243566140069 0 0 1 240 154.43328103002136
    A 118.07243566140069 118.07243566140069 0 0 1 240 347.4218476471772"
          style={{ fillOpacity: 0 }}></path>
        <text
          textAnchor="middle"
          dy=".35em"
          x="239"
          y="290"
          style={{ fill: 'rgb(68, 68, 68)' }}>
          <tspan x="239" y="290" dy="0.35em"></tspan>
        </text>
      </g>

      <g className={cx(s.vennArea, s.vennIntersection, s.vennSetsAC)}>
        <path
          data-intersection="3-4"
          d="
    M 122.41136482286723 143.75291499161503
    A 118.07243566140069 118.07243566140069 0 0 1 289.544620767403 240.24708500838491
    A 118.07243566140069 118.07243566140069 0 0 1 122.41136482286723 143.75291499161503"
          style={{ fillOpacity: 0 }}></path>
        <text
          textAnchor="middle"
          dy=".35em"
          x="171"
          y="172"
          style={{ fill: 'rgb(68, 68, 68)' }}>
          <tspan x="171" y="172" dy="0.35em"></tspan>
        </text>
      </g>

      <g className={cx(s.vennArea, s.vennIntersection, s.vennSetsBC)}>
        <path
          data-intersection="3-5"
          d="
    M 190.45535877466227 240.2473102455609
    A 118.07243566140069 118.07243566140069 0 0 1 357.5881479449092 143.75268975443905
    A 118.07243566140069 118.07243566140069 0 0 1 190.45535877466227 240.2473102455609"
          style={{ fillOpacity: 0 }}></path>
        <text
          textAnchor="middle"
          dy=".35em"
          x="308"
          y="172"
          style={{ fill: 'rgb(68, 68, 68)' }}>
          <tspan x="308" y="172" dy="0.35em"></tspan>
        </text>
      </g>

      <g
        onClick={onClick}
        className={cx(s.vennArea, s.vennIntersection, s.vennSetsABC)}>
        <path
          id="logo-symbol-wrapper"
          d="
    M 190.45535877466227 240.2473102455609
    A 118.07243566140069 118.07243566140069 0 0 1 240 154.43328103002136
    A 118.07243566140069 118.07243566140069 0 0 1 289.544620767403 240.24708500838491
    A 118.07243566140069 118.07243566140069 0 0 1 190.45535877466227 240.2473102455609"></path>
        <image
          id="logo-symbol"
          x="232"
          y="185"
          width="20"
          height="50"
          xlinkHref="
    data:image/png;base64,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
"
        />
      </g>
    </svg>
  )
}

export default VennDiagram
